// 3D animated dice — reused styling from Ludo but tinted per active player color.
import React from 'react';
import { motion } from 'framer-motion';
import { Dices } from 'lucide-react';

const DOTS = {
    1: [[50, 50]],
    2: [[28, 28], [72, 72]],
    3: [[28, 28], [50, 50], [72, 72]],
    4: [[28, 28], [72, 28], [28, 72], [72, 72]],
    5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
    6: [[28, 28], [72, 28], [28, 50], [72, 50], [28, 72], [72, 72]],
};

const COLOR_THEMES = {
    yellow: { face: 'linear-gradient(135deg, #FFF200 0%, #F5C518 60%, #B58400 100%)', dot: '#231F20', glow: 'rgba(255,242,0,0.55)' },
    red:    { face: 'linear-gradient(135deg, #FF5A5F 0%, #E31E24 60%, #7F1010 100%)', dot: '#FFFFFF', glow: 'rgba(227,30,36,0.55)' },
};

export default function SLDice({ value, rolling, onRoll, disabled, color = 'yellow', showPointer }) {
    const v = value || 1;
    const dots = DOTS[v] || DOTS[1];
    const theme = COLOR_THEMES[color] || COLOR_THEMES.yellow;
    return (
        <div className="flex items-center gap-3 sm:gap-4">
            <motion.div
                animate={rolling ? { rotateX: [0, 360, 720], rotateY: [0, 360, 720], scale: [1, 1.12, 1] } : { rotateX: 0, rotateY: 0, scale: 1 }}
                transition={{ duration: rolling ? 0.6 : 0.3 }}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center relative"
                style={{
                    background: theme.face,
                    boxShadow: `0 0 28px ${theme.glow}, 0 14px 30px rgba(0,0,0,0.5), inset 0 3px 0 rgba(255,255,255,0.6), inset 0 -4px 0 rgba(0,0,0,0.3)`,
                    transformStyle: 'preserve-3d',
                    border: '2px solid rgba(0,0,0,0.4)',
                }}
                data-testid="sl-dice"
            >
                {dots.map(([x, y], i) => (
                    <span key={i} className="absolute rounded-full" style={{
                        left: `${x}%`, top: `${y}%`, width: '14%', height: '14%',
                        background: theme.dot, transform: 'translate(-50%, -50%)',
                        boxShadow: `inset 0 -2px 1px rgba(0,0,0,0.4), 0 1px 1px rgba(255,255,255,0.4)`
                    }} />
                ))}
            </motion.div>
            <div className="relative">
                <button
                    onClick={onRoll}
                    disabled={disabled || rolling}
                    className="px-5 sm:px-6 py-3 rounded-2xl bg-gradient-to-r from-brand-yellow to-amber-400 text-brand-black font-display font-extrabold uppercase tracking-wider text-sm sm:text-base shadow-lg hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-2"
                    style={{ boxShadow: '0 8px 24px rgba(255,242,0,0.45), inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.25)' }}
                    data-testid="sl-roll-btn"
                >
                    <Dices className="h-4 w-4 sm:h-5 sm:w-5" />
                    {rolling ? 'Rolling…' : 'Roll Dice'}
                </button>
                {showPointer && !rolling && (
                    <motion.div
                        animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 1.0 }}
                        className="absolute -top-9 left-1/2 -translate-x-1/2 text-2xl pointer-events-none select-none"
                    >👇</motion.div>
                )}
            </div>
        </div>
    );
}
